import React, { Fragment, useMemo, useRef } from "react"
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material"
import { getAllTimestepsFromRows } from "@features/results/Result/ChartsContainer/TimeSeriesChart.utils"
import { utils, writeFile } from "sheetjs-style-v2"
import { alignCellsVertically, autoFitColumnsWidth, boldenHeaderRow } from "@utils/excel"
import DownloadButton from "@components/DownloadButton"
import { formatValuePythonic } from "@utils/format/pythonicFormat"

export type NodeResultRow = {
  runName: string
  scenarios: Scenario[]
}

type Scenario = {
  name: string
  nodes: NodeResultInScenario[]
}

export type NodeResultInScenario = {
  node: string
  unitFormat: string
  aggregations: Aggregation[]
}

export type Aggregation = {
  name: string
  timestepValues: Record<string, number>
}

type NodeResultsTableProps = {
  rows: NodeResultRow[]
  exportFileName: string | undefined
}

const NodeResultsTable = (props: NodeResultsTableProps) => {
  const tableRef = useRef<HTMLTableElement>(null)
  const timesteps = useMemo(() => getAllTimestepsFromRows(props.rows), [props.rows])
  const headers = useMemo(
    () => [
      "Run",
      "Scenario",
      "Node name",
      "Unit",
      "Aggregation",
      ...getAllTimestepsFromRows(props.rows),
    ],
    [props.rows],
  )
  const handleDownloadTable = () => {
    if (!tableRef.current) return

    const sheet = utils.table_to_sheet(tableRef.current, { raw: true })
    boldenHeaderRow(sheet)
    alignCellsVertically(sheet, "center")
    autoFitColumnsWidth(sheet)

    const workBook = utils.book_new()
    utils.book_append_sheet(workBook, sheet)

    writeFile(
      workBook,
      props.exportFileName ? props.exportFileName + ".xlsx" : "node-results-table.xlsx",
    )
  }

  return (
    <>
      <DownloadButton onClick={handleDownloadTable} />

      <TableContainer component={Paper}>
        <Table size="small" ref={tableRef}>
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <TableCell key={header} sx={{ fontWeight: "bold" }}>
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {props.rows.map((row) => (
              <Fragment key={row.runName}>
                {row.scenarios.map((scenario, scenarioIndex) => (
                  <Fragment key={scenario.name}>
                    {scenario.nodes.map((nodeResult, nodeIndex) => (
                      <Fragment key={nodeResult.node}>
                        {nodeResult.aggregations.map((aggregation, aggIndex) => {
                          const isFirstRowForRun =
                            scenarioIndex === 0 && nodeIndex === 0 && aggIndex === 0
                          const isFirstRowForScenario = nodeIndex === 0 && aggIndex === 0
                          const isFirstRowForNode = aggIndex === 0

                          return (
                            <TableRow
                              key={`${row.runName}-${scenario.name}-${nodeResult.node}-${aggregation.name}`}
                            >
                              {isFirstRowForRun && (
                                <TableCell rowSpan={calculateRunCellRowSpan(row)}>
                                  {row.runName}
                                </TableCell>
                              )}

                              {isFirstRowForScenario && (
                                <TableCell rowSpan={calculateScenarioCellRowSpan(scenario)}>
                                  {scenario.name}
                                </TableCell>
                              )}

                              {isFirstRowForNode && (
                                <>
                                  <TableCell rowSpan={nodeResult.aggregations.length}>
                                    {nodeResult.node}
                                  </TableCell>
                                  <TableCell rowSpan={nodeResult.aggregations.length}>
                                    {formatValuePythonic("", nodeResult.unitFormat)}
                                  </TableCell>
                                </>
                              )}

                              <TableCell>{aggregation.name}</TableCell>
                              {timesteps.map((timestep) => {
                                const value = aggregation.timestepValues[timestep]

                                return (
                                  <TableCell key={timestep}>
                                    {value === undefined
                                      ? ""
                                      : formatValuePythonic(value, nodeResult.unitFormat, false)}
                                  </TableCell>
                                )
                              })}
                            </TableRow>
                          )
                        })}
                      </Fragment>
                    ))}
                  </Fragment>
                ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

export default NodeResultsTable

const calculateScenarioCellRowSpan = (scenario: Scenario) => {
  return scenario.nodes.reduce((sum, node) => sum + node.aggregations.length, 0)
}

const calculateRunCellRowSpan = (row: NodeResultRow) => {
  return row.scenarios.reduce(
    (sum, scenarioGroup) => sum + calculateScenarioCellRowSpan(scenarioGroup),
    0,
  )
}
